import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, FileText, Calendar, Users, Briefcase, ShoppingBag, Image, Pencil, Trash2, Plus, Check, X, Eye, EyeOff, Inbox, UserCog, Shield, MapPin, Sparkles, Target, PauseCircle, RotateCw, Archive } from "lucide-react";
import UsersAdmin from "@/components/UsersAdmin";
import PlatserAdmin from "@/components/PlatserAdmin";
import GalleriEditor from "@/components/GalleriEditor";
import LocationPicker from "@/components/LocationPicker";
import TagInput from "@/components/TagInput";
import WingmanBox from "@/components/WingmanBox";
import ChangeHistoryViewer from "@/components/ChangeHistoryViewer";
import PendingDraftBanner from "@/components/PendingDraftBanner";
import PlatserOversiktPanel from "@/components/PlatserOversiktPanel";
import PapperskorgPanel from "@/components/PapperskorgPanel";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function modHeaders() { return { Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` }; }

const MODERATOR_TABS = [
  { key: "anslag", label: "Anslag", icon: FileText },
  { key: "evenemang", label: "Evenemang", icon: Calendar },
  { key: "foreningar", label: "Föreningar", icon: Users },
  { key: "tjanstekort", label: "Företag", icon: Briefcase },
];

const MASTER_TABS = [
  ...MODERATOR_TABS,
  { key: "karttipps", label: "Platser (sammelvy)", icon: MapPin, master: true },
  // Etappe 4.4 — "Marker-fixer"-Tab deprecated. Koordinatenkorrektur erfolgt jetzt
  // direkt in jedem Audit-Tab über LocationPicker mit Audit-Trail (andrad_av/senast_andrad/change_history).
  // Legacy-Komponente /app/frontend/src/components/PlatserAdmin.js und Backend-Endpoints
  // /api/mod/platser/* bleiben temporär bestehen (1-2 Wochen Beobachtung), bevor sauber gelöscht wird.
  { key: "vardagskort", label: "Vardagskort", icon: ShoppingBag, master: true },
  { key: "bilder", label: "Bilder", icon: Image, master: true },
  { key: "andringsforslag", label: "Ändringsförslag", icon: Inbox, master: true },
  { key: "users", label: "Användare", icon: UserCog, master: true },
  // Build 96.6 — Papperskorg: visar soft-deleted poster för Master/Admin.
  { key: "papperskorg", label: "Papperskorg", icon: Trash2, master: true },
];

function isMaster(user) {
  return user && (user.role === "master" || user.role === "admin");
}

export default function AdminPanel() {
  const [tab, setTab] = useState("anslag");
  const [items, setItems] = useState([]);
  const [itemsTab, setItemsTab] = useState(null); // Build 96.2 — vilken tab `items` motsvarar (för cross-edit-dispatch)
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  // Build 96.2 — cross-tab edit-dispatch från Platser-sammelvyn.
  // När en moderator klickar "Redigera" på en främmande source-rad (t.ex. förening)
  // växlar vi tab och triggar setEditId så snart items är laddade.
  const [pendingCrossEdit, setPendingCrossEdit] = useState(null); // { tab, id } | null

  // Build 94 — humanisera 422-pydantic-fel till svenska
  const formatApiError = (err, fallback = "Kunde inte spara") => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const parts = detail.map((d) => {
        const field = Array.isArray(d.loc) ? d.loc.slice(1).join(".") : "fält";
        if (d.type === "missing") return `${field} saknas`;
        if (d.type === "list_type") return `${field} måste vara en lista`;
        if (d.type === "string_too_long") return `${field} är för långt`;
        return `${field}: ${d.msg || d.type}`;
      });
      return parts.join(" · ");
    }
    return fallback;
  };

  // Build 91 — helper för taggar: array ↔ comma-separated string konvertering.
  // Backend tar emot List[str]. Användaren skriver "Natur, Historia, Foto" i UI.
  const serializeForApi = (data) => {
    const out = { ...data };
    if (typeof out.taggar === "string") {
      out.taggar = out.taggar.split(",").map(s => s.trim()).filter(Boolean);
    }
    return out;
  };
  const hydrateForUi = (item) => {
    const out = { ...item };
    if (Array.isArray(out.taggar)) out.taggar = out.taggar.join(", ");
    return out;
  };
  const [creating, setCreating] = useState(false);
  // Etappe 4.2 — change-history viewer state: { collection, docId } eller null
  const [historyTarget, setHistoryTarget] = useState(null);
  // Etappe 4.4 — Filter "Bara utan koordinater" (per session, ersätter gamla Marker-fixer)
  const [missingCoordsOnly, setMissingCoordsOnly] = useState(false);
  // Build 94c.1 — Schnellsuche nach stabiler karttipp_nr ("Nr. 37 prüfen")
  const [nrSearch, setNrSearch] = useState("");
  // Etappe 4.5 — Evenemang-kvalitetsfilter (Kommande / Pågående / Förbi / Utan datum / Avpublicerat)
  const [eventStatusFilter, setEventStatusFilter] = useState("all");
  // Build 94b — efter ett create: highlighta den nya posten 5 sek så moderatorn ser var den hamnade.
  const [highlightItemId, setHighlightItemId] = useState(null);
  useEffect(() => {
    if (!highlightItemId) return;
    const t = setTimeout(() => setHighlightItemId(null), 5000);
    return () => clearTimeout(t);
  }, [highlightItemId]);
  // wingmanFocus = { title, subtitle, ids?: string[], filter?: "Inskickat", examples?: string[], tab }
  const [wingmanFocus, setWingmanFocus] = useState(null);
  const [highlightTab, setHighlightTab] = useState(null);
  const [uploadHealth, setUploadHealth] = useState(null);
  const navigate = useNavigate();
  const user = JSON.parse(sessionStorage.getItem("mod_user") || "null");

  const fetchItems = useCallback(async () => {
    // Etappe 4.4 — "platser"-Tab deprecated. Guard kvar för fallback om bookmark öppnar gamla tab.
    // Build 96.6 — papperskorg-tabben har egen fetch i PapperskorgPanel.
    if (tab === "users" || tab === "platser" || tab === "papperskorg") {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setEditId(null);
    setCreating(false);
    setEditData({}); // Etappe 1.5 — reset state vid tab-byte så fält inte läcker mellan tabbarna
    setMissingCoordsOnly(false); // Etappe 4.4 — reset coord-filter vid tab-byte
    setEventStatusFilter("all"); // Etappe 4.5 — reset event-kvalitetsfilter vid tab-byte
    setNrSearch(""); // Build 94c.1 — reset nr-sökning vid tab-byte
    try {
      const endpoints = {
        anslag: "/mod/anslag",
        evenemang: "/mod/evenemang",
        foreningar: "/mod/foreningar",
        tjanstekort: "/mod/tjanstekort",
        vardagskort: "/mod/vardagskort",
        karttipps: "/mod/karttipps",
        bilder: "/mod/bilder",
        andringsforslag: "/mod/andringsforslag",
      };
      const { data } = await axios.get(`${API}${endpoints[tab]}`, { headers: modHeaders() });
      setItems(data);
      setItemsTab(tab); // Build 96.2 — markera vilken tab `items` motsvarar
    } catch (err) {
      if (err.isSessionExpired) {
        // Den globala interceptorn har redan tagit hand om allting (modal + draft).
      } else if (err.response?.status === 401) {
        navigate(`/mod/login?returnTo=${encodeURIComponent(window.location.pathname)}`);
      }
    }
    setLoading(false);
  }, [tab, navigate]);

  useEffect(() => {
    if (!sessionStorage.getItem("mod_token")) {
      navigate(`/mod/login?returnTo=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    fetchItems();
  }, [fetchItems, navigate]);

  // Build 96.2 — När items laddats färdig för rätt tab och vi har en pending
  // cross-edit-dispatch: öppna edit-formuläret och scrolla dit.
  useEffect(() => {
    if (!pendingCrossEdit) return;
    if (pendingCrossEdit.tab !== tab) return;
    if (itemsTab !== tab) return; // items är ännu inte uppdaterade för rätt tab
    if (loading) return;
    const it = (items || []).find((x) => x.id === pendingCrossEdit.id);
    if (!it) {
      toast.error("Posten kunde inte hittas — kanske flyttad eller borttagen.");
      setPendingCrossEdit(null);
      return;
    }
    setEditId(it.id);
    setEditData(hydrateForUi(it));
    setCreating(false);
    setPendingCrossEdit(null);
    // Mjuk scroll till edit-fältet (efter render)
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="admin-item-${it.id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, [items, itemsTab, loading, tab, pendingCrossEdit]);

  // Build 96.2 — Edit-dispatcher anropad av PlatserOversiktPanel.
  const handleOpenEditFromOversikt = ({ source, tab: targetTab, id }) => {
    if (!targetTab) return;
    if (targetTab === tab) {
      // Redan rätt tab — öppna direkt om item finns i listan
      const it = (items || []).find((x) => x.id === id);
      if (it) {
        setEditId(id);
        setEditData(hydrateForUi(it));
        setCreating(false);
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="admin-item-${id}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      } else {
        setPendingCrossEdit({ tab: targetTab, id });
      }
      return;
    }
    // Annan tab → växla + sätt pending för useEffect ovan
    setPendingCrossEdit({ tab: targetTab, id });
    setTab(targetTab);
  };

  // Smoke-test: hämta status för senaste lyckade upload (visas diskret under e-postraden)
  useEffect(() => {
    if (!sessionStorage.getItem("mod_token")) return;
    axios.get(`${API}/mod/upload-health`, { headers: modHeaders() })
      .then(r => setUploadHealth(r.data || null))
      .catch(() => setUploadHealth(null));
  }, []);

  const handleAction = async (id, action, method = "post") => {
    try {
      const endpoints = {
        anslag: `/mod/anslag/${id}/${action}`,
        evenemang: `/mod/evenemang/${id}/${action}`,
        foreningar: `/mod/foreningar/${id}/${action}`,
        tjanstekort: `/mod/tjanstekort/${id}/${action}`,
        vardagskort: `/mod/vardagskort/${id}/${action}`,
        karttipps: `/mod/karttipps/${id}/${action}`,
        bilder: `/mod/bilder/${id}/${action}`,
      };
      if (method === "delete") {
        await axios.delete(`${API}${endpoints[tab]}`, { headers: modHeaders() });
      } else {
        await axios.post(`${API}${endpoints[tab]}`, {}, { headers: modHeaders() });
      }
      fetchItems();
    } catch {}
  };

  // Build 96.17 — enhetlig moderations-livscykel (samma logik som PlatserOversiktPanel).
  // Kollektionen härleds från aktuell tab. Rör aldrig publiceringsstatus (`status`) — bara `moderation_status`.
  const setItemModerationStatus = async (id, newStatus) => {
    if (!["karttipps", "tjanstekort", "foreningar", "vardagskort", "evenemang", "anslag", "bilder"].includes(tab)) return;
    try {
      await axios.patch(
        `${API}/mod/${tab}/${id}/moderation-status`,
        { status: newStatus },
        { headers: modHeaders() },
      );
      toast.success(
        newStatus === "aktiv" ? "Aktiverad" :
        newStatus === "granskas" ? "Satt i granskning" :
        newStatus === "deaktiverad" ? "Deaktiverad" :
        newStatus === "arkiverad" ? "Arkiverad" : "Uppdaterad"
      );
      fetchItems();
    } catch (err) {
      if (err.isSessionExpired) return;
      toast.error(`Kunde inte uppdatera status: ${err.response?.data?.detail || err.message || ""}`);
    }
  };

  // Build 96.5 — Soft-Delete: typlabel + stabilt nummer per collection för dialog.
  // Backend gör status='raderad' i stället för fysisk radering. R2-filer rörs inte.
  const TAB_LABELS = {
    anslag: "Anslag",
    evenemang: "Evenemang",
    foreningar: "Förening",
    tjanstekort: "Företag",
    vardagskort: "Vardagskort",
    karttipps: "Karttipp",
    bilder: "Bild",
    andringsforslag: "Ändringsförslag",
  };
  const getNrForTab = (item, t) => {
    if (!item) return null;
    if (t === "karttipps") return item.karttipp_nr;
    if (t === "tjanstekort") return item.foretag_nr;
    if (t === "foreningar") return item.forening_nr;
    if (t === "vardagskort") return item.vardagskort_nr;
    return null;
  };

  const handleDelete = async (id, label, opts = {}) => {
    // opts: { tab?: string, item?: object, label?: string }
    const tabForDelete = opts.tab || tab;
    const typeLabel = TAB_LABELS[tabForDelete] || "Posten";
    const item = opts.item;
    const nr = item ? getNrForTab(item, tabForDelete) : null;
    const idStr = nr ? `#${nr}` : `#${String(id).slice(0, 8)}`;
    const name = label || "(utan namn)";
    // Build 96.5 — Soft-Delete-meddelande för 7 huvudkollektioner.
    // Ändringsförslag = hård-radering (moderationskö, ej publikt innehåll).
    const isHardDelete = tabForDelete === "andringsforslag";
    const confirmMsg = isHardDelete
      ? `Vill du ta bort ${typeLabel} ${idStr} – ${name}?\n\nÅtgärden går inte att ångra.`
      : `Vill du ta bort ${typeLabel} ${idStr} – ${name}?\n\n` +
        `Posten döljs från publika vyer men kan återställas av admin.`;
    if (!window.confirm(confirmMsg)) return;
    const deleteEndpoints = {
      anslag: `/mod/anslag/${id}`,
      evenemang: `/mod/evenemang/${id}`,
      foreningar: `/mod/foreningar/${id}`,
      tjanstekort: `/mod/tjanstekort/${id}`,
      vardagskort: `/mod/vardagskort/${id}`,
      karttipps: `/mod/karttipps/${id}`,
      bilder: `/mod/bilder/${id}`,
      andringsforslag: `/mod/andringsforslag/${id}`,
    };
    if (!deleteEndpoints[tabForDelete]) {
      toast.error("Radering stöds inte för denna typ");
      return;
    }
    try {
      await axios.delete(`${API}${deleteEndpoints[tabForDelete]}`, { headers: modHeaders() });
      const toastDesc = isHardDelete
        ? `${typeLabel} ${idStr} – borttaget`
        : `${typeLabel} ${idStr} – döljs nu från publika vyer`;
      toast.success("Borttaget", { description: toastDesc, duration: 3000 });
      // Optimistisk lokal update + omfetch så listan + Wingman uppdateras
      setItems((prev) => prev.filter((it) => it.id !== id));
      // Rensa eventuell wingman-fokus på just denna post
      setWingmanFocus((wf) => {
        if (!wf || !wf.ids) return wf;
        const remaining = wf.ids.filter((x) => x !== id);
        if (remaining.length === wf.ids.length) return wf;
        if (remaining.length === 0) return null;
        return { ...wf, ids: remaining };
      });
      fetchItems();
    } catch (err) {
      if (err.isSessionExpired) return; // Modal + draft skyddar redan.
      const msg = err.response?.data?.detail || err.response?.statusText || "Kunde inte radera";
      toast.error(`Radering misslyckades: ${msg}`);
    }
  };

  // Build 94 / Etappe 1.5 — Whitelist av fält som backend accepterar per tab.
  // Detta förhindrar att gamla/extra fält (t.ex. auto_archive_reason, kalla,
  // senast_kontrollerad, status) skickas med i PUT-payloaden och hamnar i
  // Pydantic-validation som "extra ignored" eller — vid framtida strict-mode —
  // som 422. Säkrare och tydligare än att posta hela item-state.
  const ALLOWED_EDIT_FIELDS = {
    anslag: ["rubrik", "titel", "beskrivning", "ort", "adress", "lat", "lng", "google_maps_url", "avsandare_namn", "kontakt_telefon", "kontakt_epost", "giltigt_tom", "pristyp", "prisbelopp", "webbplats", "facebook_url", "instagram_url", "youtube_url", "kalltext", "kalla_lank", "ansvarig_vard", "sakerhetsnotering", "riskomrade", "kvalitetsstatus", "notiz_admin", "andrad_av", "andringsnotering"],
    evenemang: ["titel", "kortbeskrivning", "beskrivning", "datum", "datum_slut", "tid", "tid_slut", "plats", "adress", "lat", "lng", "google_maps_url", "arrangor", "kontaktvag", "webbplats", "facebook_url", "instagram_url", "youtube_url", "media_url", "media_typ", "media_beskrivning", "ansvarig_vard", "sakerhetsnotering", "riskomrade", "kvalitetsstatus", "notiz_admin", "andrad_av", "andringsnotering"],
    foreningar: ["namn", "kortbeskrivning", "beskrivning", "ort", "adress", "lat", "lng", "google_maps_url", "kontaktperson", "kontakt_epost", "kontakt_telefon", "webbplats", "facebook_url", "instagram_url", "youtube_url", "media_url", "media_typ", "media_beskrivning", "kalltext", "kalla_lank", "ansvarig_vard", "sakerhetsnotering", "riskomrade", "kvalitetsstatus", "notiz_admin", "andrad_av", "andringsnotering", "kartmeny"],
    tjanstekort: ["foretagsnamn", "kortbeskrivning", "beskrivning", "ort", "adress", "lat", "lng", "google_maps_url", "kontaktperson", "kontakt_epost", "kontakt_telefon", "webbplats", "facebook_url", "instagram_url", "youtube_url", "kalltext", "kalla_lank", "media_url", "media_typ", "media_beskrivning", "ansvarig_vard", "sakerhetsnotering", "riskomrade", "kvalitetsstatus", "notiz_admin", "andrad_av", "andringsnotering", "kartmeny"],
    vardagskort: ["rubrik", "kortbeskrivning", "beskrivning", "ort", "adress", "lat", "lng", "google_maps_url", "omrade", "kontakt_telefon", "kontakt_epost", "extern_lank", "kalltext", "kalla_lank", "webbplats", "facebook_url", "instagram_url", "youtube_url", "media_url", "media_typ", "media_beskrivning", "ansvarig_vard", "sakerhetsnotering", "riskomrade", "kvalitetsstatus", "notiz_admin", "andrad_av", "andringsnotering", "kartmeny"],
    karttipps: ["namn", "kategori", "main_category", "omrade", "kortbeskrivning", "beskrivning", "ort", "adress", "lat", "lng", "google_maps_url", "kontakt_telefon", "kontakt_epost", "webbplats", "facebook_url", "instagram_url", "youtube_url", "kalltext", "kalla_lank", "media_url", "media_typ", "media_beskrivning", "notiz_admin", "taggar", "ansvarig_vard", "sakerhetsnotering", "riskomrade", "kvalitetsstatus", "andrad_av", "andringsnotering", "kartmeny"],
    // Build 94c.6 — Mod-skapad bild: minimal whitelist för create-flödet (edit av bilder sker via publicera/avpublicera-knappar, inte fält-edit).
    bilder: ["titel", "beskrivning", "fotograf", "ort", "bild_url", "tema", "media_typ", "samtycke", "andrad_av"],
  };
  const pickEditPayload = (tabKey, data) => {
    const allowed = ALLOWED_EDIT_FIELDS[tabKey];
    if (!allowed) return data;
    const out = {};
    for (const k of allowed) {
      if (data[k] !== undefined) out[k] = data[k];
    }
    return out;
  };

  const handleSaveEdit = async (id) => {
    // Etappe 3 + 4.1 + 4.2 + 4.3 — andrad_av Pflicht beim Edit för alla Audit-Tabs
    if (["karttipps", "foreningar", "tjanstekort", "evenemang", "vardagskort", "anslag"].includes(tab)) {
      if (!editData.andrad_av || !editData.andrad_av.trim()) {
        toast.error("Ange namn på den som ändrar posten innan du sparar.");
        return;
      }
    }
    try {
      const endpoints = {
        anslag: `/mod/anslag/${id}/edit`,
        evenemang: `/mod/evenemang/${id}/edit`,
        foreningar: `/mod/foreningar/${id}/edit`,
        tjanstekort: `/mod/tjanstekort/${id}/edit`,
        vardagskort: `/mod/vardagskort/${id}/edit`,
        karttipps: `/mod/karttipps/${id}/edit`,
        andringsforslag: `/mod/andringsforslag/${id}/edit`,
      };
      // Build 94 — whitelist + serializeForApi.
      const cleaned = pickEditPayload(tab, serializeForApi(editData));
      await axios.put(`${API}${endpoints[tab]}`, cleaned, { headers: modHeaders() });
      toast.success("Sparat");
      setEditId(null);
      fetchItems();
    } catch (err) {
      if (err.isSessionExpired) return; // Modal + draft skyddar redan.
      toast.error(`Sparning misslyckades: ${formatApiError(err, "Kunde inte spara")}`);
    }
  };

  const handleCreate = async () => {
    // Build 94 — frontend-validering INNAN POST: tydliga felmeddelanden istället för 422.
    if (tab === "karttipps") {
      if (!editData.namn || !editData.namn.trim()) {
        toast.error("Titel / Platsnamn krävs.");
        return;
      }
      if (!editData.kategori) {
        toast.error("Välj en kategori.");
        return;
      }
      if (typeof editData.lat !== "number" || typeof editData.lng !== "number") {
        toast.error("Plats på karta krävs — klicka på kartan eller klistra in koordinater.");
        return;
      }
      // Etappe 3 — Ändrad av Pflicht
      if (!editData.andrad_av || !editData.andrad_av.trim()) {
        toast.error("Ange namn på den som ändrar posten innan du sparar.");
        return;
      }
    }
    // Etappe 1.5 — samma defensiva check för evenemang
    if (tab === "evenemang") {
      if (!editData.titel || !editData.titel.trim()) { toast.error("Titel krävs."); return; }
      if (!editData.beskrivning || !editData.beskrivning.trim()) { toast.error("Beskrivning krävs."); return; }
      if (!editData.datum) { toast.error("Datum krävs."); return; }
      if (!editData.plats || !editData.plats.trim()) { toast.error("Plats krävs."); return; }
      // Etappe 4.2 — Ändrad av Pflicht
      if (!editData.andrad_av || !editData.andrad_av.trim()) {
        toast.error("Ange namn på den som ändrar posten innan du sparar.");
        return;
      }
    }
    if (tab === "foreningar") {
      if (!editData.namn || !editData.namn.trim()) { toast.error("Namn krävs."); return; }
      if (!editData.beskrivning || !editData.beskrivning.trim()) { toast.error("Beskrivning krävs."); return; }
      // Etappe 4.1 — Ändrad av Pflicht
      if (!editData.andrad_av || !editData.andrad_av.trim()) {
        toast.error("Ange namn på den som ändrar posten innan du sparar.");
        return;
      }
    }
    if (tab === "tjanstekort") {
      if (!editData.foretagsnamn || !editData.foretagsnamn.trim()) { toast.error("Företagsnamn krävs."); return; }
      if (!editData.beskrivning || !editData.beskrivning.trim()) { toast.error("Beskrivning krävs."); return; }
      // Etappe 4.1 — Ändrad av Pflicht
      if (!editData.andrad_av || !editData.andrad_av.trim()) {
        toast.error("Ange namn på den som ändrar posten innan du sparar.");
        return;
      }
    }
    if (tab === "vardagskort") {
      if (!editData.rubrik || !editData.rubrik.trim()) { toast.error("Rubrik krävs."); return; }
      if (!editData.beskrivning || !editData.beskrivning.trim()) { toast.error("Beskrivning krävs."); return; }
      // Etappe 4.2 — Ändrad av Pflicht
      if (!editData.andrad_av || !editData.andrad_av.trim()) {
        toast.error("Ange namn på den som ändrar posten innan du sparar.");
        return;
      }
    }
    // Build 94c.6 — Bild-create validering
    if (tab === "bilder") {
      if (!editData.titel || !editData.titel.trim()) { toast.error("Titel krävs."); return; }
      if (!editData.fotograf || !editData.fotograf.trim()) { toast.error("Fotograf / källa krävs."); return; }
      if (!editData.bild_url || !editData.bild_url.trim()) { toast.error("Bild-URL krävs."); return; }
      if (!editData.samtycke) { toast.error("Bekräfta att källa och rättigheter är kontrollerade."); return; }
      if (!editData.andrad_av || !editData.andrad_av.trim()) {
        toast.error("Ange namn på den som skapar posten innan du sparar.");
        return;
      }
    }
    if (tab === "anslag") {
      if (!editData.rubrik) { toast.error("Välj rubrik."); return; }
      if (!editData.titel || !editData.titel.trim()) { toast.error("Titel krävs."); return; }
      if (!editData.beskrivning || !editData.beskrivning.trim()) { toast.error("Beskrivning krävs."); return; }
      // Etappe 4.3 — Ändrad av Pflicht
      if (!editData.andrad_av || !editData.andrad_av.trim()) {
        toast.error("Ange namn på den som ändrar posten innan du sparar.");
        return;
      }
    }
    try {
      const endpoints = {
        anslag: "/mod/anslag/create",
        evenemang: "/mod/evenemang/create",
        foreningar: "/mod/foreningar/create",
        tjanstekort: "/mod/tjanstekort/create",
        vardagskort: "/mod/vardagskort/create",
        karttipps: "/mod/karttipps/create",
        bilder: "/mod/bilder/create",
      };
      // Build 94 — använd serializeForApi + whitelist + lägg till publish.
      const cleaned = pickEditPayload(tab, serializeForApi(editData));
      const payload = { ...cleaned, publish: true };
      const { data: createdData } = await axios.post(`${API}${endpoints[tab]}`, payload, { headers: modHeaders() });
      toast.success("Skapat");
      setCreating(false);
      setEditData({});
      // Build 94b — efter skapande: rensa filter så den nya posten garanterat syns
      // (annars hamnar den utanför aktiv Wingman-fokus eller event-statusfilter).
      setWingmanFocus(null);
      if (tab === "evenemang") setEventStatusFilter("all");
      setMissingCoordsOnly(false);
      // Highlighta den nya posten i 5 sek så moderatorn ser var den hamnade.
      if (createdData?.id) setHighlightItemId(createdData.id);
      fetchItems();
    } catch (err) {
      if (err.isSessionExpired) return; // Modal + draft skyddar redan.
      toast.error(`Skapande misslyckades: ${formatApiError(err, "Kunde inte skapa")}`);
    }
  };

  const handleMarkAndringsforslag = async (id) => {
    try {
      await axios.post(`${API}/mod/andringsforslag/${id}/markera-hanterat`, {}, { headers: modHeaders() });
      fetchItems();
    } catch {}
  };

  const getFields = () => {
    // Build 94b — förenklat: bara "Medielänk" + valfri beskrivning. Typ härleds automatiskt
    // (YouTube → Video, .mp3/.wav → Audio, .pdf/.docx → Dokument, annars → Artikel).
    // `media_typ` finns kvar internt för bakåtkompatibilitet men visas inte längre i formuläret.
    const MEDIA_FIELDS = [
      { key: "media_url", label: "Medielänk (URL, valfritt)", hint: "Länk till YouTube, webbplats, dokument eller annan extern information. Typ av media känns igen automatiskt." },
      { key: "media_beskrivning", label: "Medie-beskrivning (kort, valfritt)", type: "textarea", hint: "T.ex. 'Film om Fjällsjö kyrka från 2024' – visas tillsammans med länken." },
    ];
    // Build 96.1 — Kartmeny: konsoliderat publikt filter (7 grupper, alfabetisk).
    // "Visa allt" är UI-reset publikt och kan INTE sparas här.
    // Sorteringen följer sichtbart label via localeCompare("sv").
    const KARTMENY_FIELD = {
      key: "kartmeny",
      label: "Visas under kartmeny",
      type: "select",
      hint: "Välj vilken knapp under \"Vad söker du i bygden?\" posten ska visas under. Lämna tomt om du vill att fallback-logiken (kategori/tag) ska gälla.",
      options: [
        { value: "folk_foreningar",    label: "Folk & föreningar" },
        { value: "friluft_idrott",     label: "Friluft & idrott" },
        { value: "hemma_byarna",       label: "Hemma i byarna" },
        { value: "loppis_fika",        label: "Loppis & fika" },
        { value: "praktisk_hjalp",     label: "Praktisk hjälp" },
        { value: "utflykter_historia", label: "Utflykter & historia" },
        { value: "vardag",             label: "Vardag" },
      ],
    };
    switch (tab) {
      case "anslag": return [
        { key: "rubrik", label: "Rubrik", type: "select", options: ["Hjälp i vardagen", "Hand mot hand", "Köp & sälj lokalt"] },
        { key: "titel", label: "Titel" }, { key: "beskrivning", label: "Beskrivning", type: "textarea" },
        { key: "ort", label: "Ort" }, { key: "adress", label: "Adress (gata + nr)" },
        { key: "__location", label: "Plats på karta", type: "location" },
        { key: "avsandare_namn", label: "Avsändare" },
        { key: "kontakt_telefon", label: "Telefon" }, { key: "kontakt_epost", label: "E-post" },
        { key: "giltigt_tom", label: "Giltigt till och med (YYYY-MM-DD, valfritt)" },
        { key: "pristyp", label: "Pristyp (valfritt)" },
        { key: "prisbelopp", label: "Prisbelopp (valfritt)" },
        // ── Sociala länkar — Etappe 4.3 ──
        { key: "webbplats", label: "Webbplats (URL)" },
        { key: "facebook_url", label: "Facebook-länk (URL)" },
        { key: "instagram_url", label: "Instagram-länk (URL)" },
        { key: "youtube_url", label: "YouTube-länk (URL — alla format: watch, youtu.be, embed)" },
        // ── Källa — Etappe 4.3 ──
        { key: "kalltext", label: "Källa (namn)" },
        { key: "kalla_lank", label: "Länk till källa" },
        // ── Ansvar och säkerhet — Etappe 4.3 ──
        { key: "ansvarig_vard", label: "Ansvarig / värd (kontaktperson)" },
        { key: "sakerhetsnotering", label: "Säkerhetsnotering (sakligt)", type: "textarea" },
        { key: "riskomrade", label: "Riskområde (markera om anslaget ska visas med dezent varning)", type: "checkbox" },
        // ── Intern moderering — Etappe 4.3 ──
        { key: "kvalitetsstatus", label: "Kvalitetsstatus (intern markering)", type: "select", options: [
          "", "Verifierad", "Uppdaterad", "Behöver kontrolleras", "Information saknas", "Rekommenderad av redaktionen",
        ]},
        { key: "notiz_admin", label: "Intern anteckning (bara admin ser detta)", type: "textarea" },
        // ── Ändringsuppgifter — Etappe 4.3 (Pflicht: andrad_av) ──
        { key: "andrad_av", label: "Ändrad av", hint: "Ange namn på den som ändrar posten.", required: true },
        { key: "andringsnotering", label: "Ändringsnotering (valfri kort kommentar om vad som ändrades)" },
        { key: "senast_andrad", label: "Senast ändrad (sätts automatiskt)", type: "readonly" },
      ];
      case "evenemang": return [
        { key: "titel", label: "Titel" },
        { key: "kortbeskrivning", label: "Kort exposé (max 300 tecken, visas i listor)", type: "kortbeskrivning" },
        { key: "beskrivning", label: "Beskrivning", type: "textarea" },
        { key: "datum", label: "Startdatum", type: "date", hint: "Datum då eventet börjar. För endagsaktiviteter — fyll bara i detta." },
        { key: "datum_slut", label: "Slutdatum (valfritt, för flerdagars)", type: "date", hint: "Lämna tomt för endagsaktiviteter. Annars sista dagen — t.ex. 2026-07-31." },
        { key: "tid", label: "Starttid", type: "time", hint: "Valfritt. Ex: 11:00." },
        { key: "tid_slut", label: "Sluttid (valfritt)", type: "time", hint: "Lämna tomt om eventet inte har en bestämd sluttid. Ex: 15:00." },
        { key: "plats", label: "Plats (namn på lokalen)" },
        { key: "adress", label: "Adress (gata + nr)" },
        { key: "__location", label: "Plats på karta", type: "location" },
        { key: "arrangor", label: "Arrangör" }, { key: "kontaktvag", label: "Kontakt" },
        // Etappe 1.6 — sociala länkar (standardiserat referensschema)
        { key: "webbplats", label: "Webbplats (URL)" },
        { key: "facebook_url", label: "Facebook-länk (URL)" },
        { key: "instagram_url", label: "Instagram-länk (URL)" },
        { key: "youtube_url", label: "YouTube-länk (URL — alla format: watch, youtu.be, embed)" },
        ...MEDIA_FIELDS,
        // ── Ansvar och säkerhet — Etappe 4.2 ──
        { key: "ansvarig_vard", label: "Ansvarig / värd (arrangör, kontaktperson)" },
        { key: "sakerhetsnotering", label: "Säkerhetsnotering (sakligt — t.ex. begränsad tillgänglighet)", type: "textarea" },
        { key: "riskomrade", label: "Riskområde (markera om eventet ska visas med dezent varning)", type: "checkbox" },
        // ── Intern moderering — Etappe 4.2 ──
        { key: "kvalitetsstatus", label: "Kvalitetsstatus (intern markering)", type: "select", options: [
          "", "Verifierad", "Uppdaterad", "Behöver kontrolleras", "Information saknas", "Rekommenderad av redaktionen",
        ]},
        { key: "notiz_admin", label: "Intern anteckning (bara admin ser detta)", type: "textarea" },
        // ── Ändringsuppgifter — Etappe 4.2 (Pflicht: andrad_av) ──
        { key: "andrad_av", label: "Ändrad av", hint: "Ange namn på den som ändrar posten.", required: true },
        { key: "andringsnotering", label: "Ändringsnotering (valfri kort kommentar om vad som ändrades)" },
        { key: "senast_andrad", label: "Senast ändrad (sätts automatiskt)", type: "readonly" },
      ];
      case "foreningar": return [
        // Etappe 4.1 — gemeinsame Moderationslogik wie Platser
        // ── Grunduppgifter ──
        { key: "namn", label: "Namn" },
        { key: "kortbeskrivning", label: "Kort exposé (max 300 tecken, visas i listor)", type: "kortbeskrivning" },
        { key: "beskrivning", label: "Beskrivning", type: "textarea" },
        // ── Plats ──
        { key: "ort", label: "Ort" }, { key: "adress", label: "Adress (gata + nr)" },
        { key: "__location", label: "Plats på karta", type: "location" },
        // ── Kontakt ──
        { key: "kontaktperson", label: "Kontaktperson" },
        { key: "kontakt_telefon", label: "Telefon" }, { key: "kontakt_epost", label: "E-post" },
        // ── Sociala länkar ──
        { key: "webbplats", label: "Webbplats (URL)" },
        { key: "facebook_url", label: "Facebook-länk (URL)" },
        { key: "instagram_url", label: "Instagram-länk (URL)" },
        { key: "youtube_url", label: "YouTube-länk (URL — alla format: watch, youtu.be, embed)" },
        // ── Media ──
        ...MEDIA_FIELDS,
        // ── Källa ──
        { key: "kalltext", label: "Källa (namn)" },
        { key: "kalla_lank", label: "Länk till källa" },
        // ── Ansvar och säkerhet — Etappe 4.1 ──
        { key: "ansvarig_vard", label: "Ansvarig / värd (ordförande, kontaktperson)" },
        { key: "sakerhetsnotering", label: "Säkerhetsnotering (sakligt — t.ex. tillgänglighet)", type: "textarea" },
        { key: "riskomrade", label: "Riskområde (markera om föreningen ska visas med dezent varning)", type: "checkbox" },
        // ── Intern moderering — Etappe 4.1 ──
        { key: "kvalitetsstatus", label: "Kvalitetsstatus (intern markering)", type: "select", options: [
          "", "Verifierad", "Uppdaterad", "Behöver kontrolleras", "Information saknas", "Rekommenderad av redaktionen",
        ]},
        { key: "notiz_admin", label: "Intern anteckning (bara admin ser detta)", type: "textarea" },
        // ── Kartmeny — Build 96.1 ──
        KARTMENY_FIELD,
        // ── Ändringsuppgifter — Etappe 4.1 (Pflicht: andrad_av) ──
        { key: "andrad_av", label: "Ändrad av", hint: "Ange namn på den som ändrar posten.", required: true },
        { key: "andringsnotering", label: "Ändringsnotering (valfri kort kommentar om vad som ändrades)" },
        { key: "senast_andrad", label: "Senast ändrad (sätts automatiskt)", type: "readonly" },
      ];
      case "tjanstekort": return [
        // Etappe 4.1 — gemeinsame Moderationslogik wie Platser
        // ── Grunduppgifter ──
        { key: "foretagsnamn", label: "Företagsnamn" },
        { key: "kortbeskrivning", label: "Kort exposé (max 300 tecken, visas i listor)", type: "kortbeskrivning" },
        { key: "beskrivning", label: "Beskrivning", type: "textarea" },
        // ── Plats ──
        { key: "ort", label: "Ort" }, { key: "adress", label: "Adress (gata + nr)" },
        { key: "__location", label: "Plats på karta", type: "location" },
        // ── Kontakt ──
        { key: "kontaktperson", label: "Kontaktperson" },
        { key: "kontakt_telefon", label: "Telefon" },
        { key: "kontakt_epost", label: "E-post" },
        // ── Sociala länkar ──
        { key: "webbplats", label: "Webbplats (URL)" },
        { key: "facebook_url", label: "Facebook-länk (URL)" },
        { key: "instagram_url", label: "Instagram-länk (URL)" },
        { key: "youtube_url", label: "YouTube-länk (URL — alla format: watch, youtu.be, embed)" },
        // ── Media ──
        ...MEDIA_FIELDS,
        // ── Källa ──
        { key: "kalltext", label: "Källa (namn)" },
        { key: "kalla_lank", label: "Länk till källa" },
        // ── Ansvar och säkerhet — Etappe 4.1 ──
        { key: "ansvarig_vard", label: "Ansvarig / värd (kontaktperson, ägare)" },
        { key: "sakerhetsnotering", label: "Säkerhetsnotering (sakligt — t.ex. öppettider, tillgänglighet)", type: "textarea" },
        { key: "riskomrade", label: "Riskområde (markera om företaget ska visas med dezent varning)", type: "checkbox" },
        // ── Intern moderering — Etappe 4.1 ──
        { key: "kvalitetsstatus", label: "Kvalitetsstatus (intern markering)", type: "select", options: [
          "", "Verifierad", "Uppdaterad", "Behöver kontrolleras", "Information saknas", "Rekommenderad av redaktionen",
        ]},
        { key: "notiz_admin", label: "Intern anteckning (bara admin ser detta)", type: "textarea" },
        // ── Kartmeny — Build 96.1 ──
        KARTMENY_FIELD,
        // ── Ändringsuppgifter — Etappe 4.1 (Pflicht: andrad_av) ──
        { key: "andrad_av", label: "Ändrad av", hint: "Ange namn på den som ändrar posten.", required: true },
        { key: "andringsnotering", label: "Ändringsnotering (valfri kort kommentar om vad som ändrades)" },
        { key: "senast_andrad", label: "Senast ändrad (sätts automatiskt)", type: "readonly" },
      ];
      case "vardagskort": return [
        { key: "rubrik", label: "Rubrik" },
        { key: "kortbeskrivning", label: "Kort exposé (max 300 tecken, visas i listor)", type: "kortbeskrivning" },
        { key: "beskrivning", label: "Beskrivning", type: "textarea" },
        { key: "omrade", label: "Område", type: "select", options: ["Handla & vardag", "Service & fordon", "Vård & trygghet"] },
        { key: "ort", label: "Ort" }, { key: "adress", label: "Adress (gata + nr)" },
        { key: "__location", label: "Plats på karta", type: "location" },
        { key: "kontakt_telefon", label: "Telefon" },
        { key: "kontakt_epost", label: "E-post" },
        { key: "extern_lank", label: "Extern länk" },
        // ── Sociala länkar — Etappe 4.2 ──
        { key: "webbplats", label: "Webbplats (URL)" },
        { key: "facebook_url", label: "Facebook-länk (URL)" },
        { key: "instagram_url", label: "Instagram-länk (URL)" },
        { key: "youtube_url", label: "YouTube-länk (URL — alla format: watch, youtu.be, embed)" },
        ...MEDIA_FIELDS,
        // ── Källa — Etappe 4.2 ──
        { key: "kalltext", label: "Källa (namn)" },
        { key: "kalla_lank", label: "Länk till källa" },
        // ── Ansvar och säkerhet — Etappe 4.2 ──
        { key: "ansvarig_vard", label: "Ansvarig / värd (kontaktperson, leverantör)" },
        { key: "sakerhetsnotering", label: "Säkerhetsnotering (sakligt — t.ex. öppettider, tillgänglighet)", type: "textarea" },
        { key: "riskomrade", label: "Riskområde (markera om kortet ska visas med dezent varning)", type: "checkbox" },
        // ── Intern moderering — Etappe 4.2 ──
        { key: "kvalitetsstatus", label: "Kvalitetsstatus (intern markering)", type: "select", options: [
          "", "Verifierad", "Uppdaterad", "Behöver kontrolleras", "Information saknas", "Rekommenderad av redaktionen",
        ]},
        { key: "notiz_admin", label: "Intern anteckning (bara admin ser detta)", type: "textarea" },
        // ── Kartmeny — Build 96.1 ──
        KARTMENY_FIELD,
        // ── Ändringsuppgifter — Etappe 4.2 (Pflicht: andrad_av) ──
        { key: "andrad_av", label: "Ändrad av", hint: "Ange namn på den som ändrar posten.", required: true },
        { key: "andringsnotering", label: "Ändringsnotering (valfri kort kommentar om vad som ändrades)" },
        { key: "senast_andrad", label: "Senast ändrad (sätts automatiskt)", type: "readonly" },
      ];
      case "karttipps": return [
        // Build 90 — Platser/Karttipps som fullvärdig innehållstyp (parallellt med Företag/Föreningar).
        // ── Grunduppgifter ──
        { key: "namn", label: "Titel / Platsnamn" },
        // Build 96.7 — det gamla "Kategori (huvudkategori)"-dropdownet är helt borttaget.
        // Ersättningsfält: `kartmeny` (konsoliderat publikt kartmeny med 7 huvudgrupper).
        // "Visa allt" är UI-reset som backend tolkar som `kartmeny=null` (fallback-logik tar över).
        { key: "kartmeny", label: "Vad söker du i bygden?", type: "select", emptyLabel: "Visa allt", options: [
          { value: "folk_foreningar",    label: "Folk & föreningar" },
          { value: "friluft_idrott",     label: "Friluft & idrott" },
          { value: "hemma_byarna",       label: "Hemma i byarna" },
          { value: "loppis_fika",        label: "Loppis & fika" },
          { value: "praktisk_hjalp",     label: "Praktisk hjälp" },
          { value: "utflykter_historia", label: "Utflykter & historia" },
          { value: "vardag",             label: "Vardag" },
        ]},
        // Build 96.3 — `main_category` (Build 95) är härmed **legacy**.
        // Fältet bevaras i DB av historiska skäl, men visas inte längre i admin-
        // formuläret. Den nya `Kartmeny` (Build 96) är den enda styrande
        // logiken för publika kartmenyn.
        { key: "omrade", label: "Område (lokal identitet)", hint: "Lokalt område, t.ex. Rossön, Backe, Älgön, Bodum. Visas inte som kartfilter, används för senare gruppering." },
        { key: "kortbeskrivning", label: "Kort exposé (max 300 tecken, visas i listor)", type: "kortbeskrivning" },
        { key: "beskrivning", label: "Beskrivning (ausführlich)", type: "textarea" },
        // ── Plats / position ──
        { key: "ort", label: "Ort" }, { key: "adress", label: "Adress (gata + nr)" },
        { key: "__location", label: "Plats på karta", type: "location" },
        // ── Taggar / funktioner ──
        { key: "taggar", label: "Taggar / funktioner (mehrfachfunktion — klicka förslagen, alphabetisk lista)", type: "tags" },
        // ── Sociala länkar ──
        { key: "webbplats", label: "Webbplats (URL)" },
        { key: "facebook_url", label: "Facebook-länk (URL)" },
        { key: "instagram_url", label: "Instagram-länk (URL)" },
        { key: "youtube_url", label: "YouTube-länk (URL — alla format: watch, youtu.be, embed)" },
        // ── Media ──
        ...MEDIA_FIELDS,
        // ── Källa ──
        { key: "kalltext", label: "Källa (namn)" },
        { key: "kalla_lank", label: "Länk till källa" },
        // ── Kontakt ──
        { key: "kontakt_telefon", label: "Telefon" }, { key: "kontakt_epost", label: "E-post" },
        // ── Ansvar och säkerhet — Etappe 3 ──
        { key: "ansvarig_vard", label: "Ansvarig / värd (gastgivare, förening, kontaktperson)" },
        { key: "sakerhetsnotering", label: "Säkerhetsnotering (sakligt — t.ex. halt is, dåligt underlag)", type: "textarea" },
        { key: "riskomrade", label: "Riskområde (markera om platsen ska visas med dezent varning)", type: "checkbox" },
        // ── Intern moderering — Etappe 3 ──
        { key: "kvalitetsstatus", label: "Kvalitetsstatus (intern markering)", type: "select", options: [
          "", "Verifierad", "Uppdaterad", "Behöver kontrolleras", "Information saknas", "Rekommenderad av redaktionen",
        ]},
        { key: "notiz_admin", label: "Intern anteckning (bara admin ser detta)", type: "textarea" },
        // ── Kartmeny — Build 96.1 ──
        KARTMENY_FIELD,
        // ── Ändringsuppgifter — Etappe 3 (Pflicht: andrad_av) ──
        { key: "andrad_av", label: "Ändrad av", hint: "Ange namn på den som ändrar posten.", required: true },
        { key: "andringsnotering", label: "Ändringsnotering (valfri kort kommentar om vad som ändrades)" },
        { key: "senast_andrad", label: "Senast ändrad (sätts automatiskt)", type: "readonly" },
      ];
      // Build 94c.6 — Bilder: minimal create-flöde med rättighetsfält
      case "bilder": return [
        { key: "titel", label: "Titel / motiv", required: true, hint: "Kort beskrivande titel, t.ex. 'Solnedgång över Backesjön'." },
        { key: "fotograf", label: "Fotograf / källa", required: true, hint: "Vem äger rättigheterna eller var kommer bilden ifrån?" },
        { key: "bild_url", label: "Bild-URL", required: true, hint: "Direktlänk till bilden. Använd Media spegla först om bilden ligger externt." },
        { key: "ort", label: "Ort (valfritt)" },
        { key: "beskrivning", label: "Beskrivning / alt-text", type: "textarea", hint: "Beskriv bilden för skärmläsare och sökmotorer — vad syns på bilden?" },
        { key: "tema", label: "Tema / kategori (valfritt)", type: "select", options: ["", "natur", "byliv", "evenemang", "historia", "tradition", "vinter", "sommar"] },
        { key: "media_typ", label: "Mediatyp", type: "select", options: ["foto", "video"] },
        { key: "samtycke", label: "Källa & rättigheter kontrollerade", type: "checkbox", required: true, hint: "Bekräfta att fotograf är angiven och att du har rätt att publicera bilden." },
        { key: "andrad_av", label: "Skapad av", required: true, hint: "Ange namn på den som lägger till bilden." },
      ];
      default: return [];
    }
  };

  const getTitle = (item) => {
    return item.titel || item.namn || item.foretagsnamn || item.rubrik || "–";
  };

  const getStatus = (item) => item.status || "–";
  const isPublished = (item) => ["Publicerat", "Publicerad"].includes(item.status);

  return (
    <div data-testid="admin-panel" className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <PendingDraftBanner urlPattern="/api/mod" onSuccess={() => fetchItems()} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl font-medium text-ink">{isMaster(user) ? "Admin" : "Moderator"}</h1>
            <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${isMaster(user) ? "bg-falu text-parchment" : "bg-pine text-parchment"}`}>
              {isMaster(user) ? <><Shield size={11} /> Full åtkomst</> : "Begränsad"}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">{user?.name || "Användare"} &middot; {user?.email}</p>
          {!isMaster(user) && (
            <p className="text-xs text-bark/60 mt-1">Du kan redigera anslag, evenemang, föreningar och företag.</p>
          )}
          <UploadHealthLine health={uploadHealth} />
        </div>
        <div className="flex items-center gap-4">
          {isMaster(user) && (
            <Link to="/mod/gpx" data-testid="link-gpx-verktyg" className="hidden sm:inline-flex items-center gap-1 text-xs text-bark hover:text-pine no-underline border border-warm-border px-2 py-1 rounded-sm">
              GPX-verktyg
            </Link>
          )}
          <Link to="/mod/event-sources" data-testid="link-event-sources" className="hidden sm:inline-flex items-center gap-1 text-xs text-bark hover:text-pine no-underline border border-warm-border px-2 py-1 rounded-sm">
            Evenemang-källor
          </Link>
          {/* Build 96.5 — "+ Skapa plats" är borttagen ur den övre verktygsraden.
              Skapa nya karttipps görs nu via "+ Skapa ny karttipp" inne i
              Platser-sammelvyn (tab "Platser") där det fackligt hör hemma. */}
          {isMaster(user) && (
            <Link to="/mod/media-mirror" data-testid="link-media-mirror" className="hidden sm:inline-flex items-center gap-1 text-xs text-bark hover:text-pine no-underline border border-warm-border px-2 py-1 rounded-sm">
              Media spegla
            </Link>
          )}
          <button onClick={() => { sessionStorage.clear(); navigate("/mod/login"); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-ink">
            <LogOut size={16} /> Logga ut
          </button>
        </div>
      </div>

      {/* Wingman – moderator-checklista (Phase 1, regelbaserad) */}
      <WingmanBox onJumpToTab={(tabKey, focusPayload) => {
        // eslint-disable-next-line no-console
        console.log("[Wingman] click received → tab:", tabKey, "payload:", focusPayload);
        // Specialfall: källbevakningsobjekt navigerar till /mod/event-sources
        if (tabKey === "event-sources") {
          navigate("/mod/event-sources");
          return;
        }
        // Specialfall: bostadsbevakning till dedikerad sida
        if (tabKey === "property-watch") {
          navigate("/mod/property-watch");
          return;
        }
        const allowedKeys = (isMaster(user) ? MASTER_TABS : MODERATOR_TABS).map(t => t.key);
        if (!allowedKeys.includes(tabKey)) {
          toast.error(`Wingman: tab "${tabKey}" inte tillgänglig för din roll`);
          // eslint-disable-next-line no-console
          console.warn(`[Wingman] tab "${tabKey}" är inte tillåten för aktuell roll`);
          return;
        }
        // Omedelbar visuell bekräftelse
        toast.success(`Wingman → ${tabKey}`, {
          description: focusPayload?.title || "Filter applicerat",
          duration: 2500,
        });
        setTab(tabKey);
        setWingmanFocus(focusPayload ? { ...focusPayload, tab: tabKey } : null);
        setEditId(null);
        setCreating(false);
        setHighlightTab(tabKey);
        setTimeout(() => setHighlightTab(null), 1800);
        // Scrolla alltid till banner-området så det är tydligt vad som hänt
        setTimeout(() => {
          const el = document.querySelector("[data-testid='wingman-focus-banner']") ||
                     document.querySelector("[data-testid='admin-tabs']");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
      }} />

      {/* Wingman-Fokus-Banner: visas så fort man klickar på en Wingman-rad */}
      {wingmanFocus && (
        <div
          data-testid="wingman-focus-banner"
          role="status"
          aria-live="polite"
          className="mb-4 bg-falu/10 border-2 border-falu/40 rounded-sm overflow-hidden"
        >
          <div className="flex items-start gap-3 px-4 sm:px-5 py-3.5">
            <Target size={20} className="text-falu shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-bold text-falu mb-0.5 flex items-center gap-1.5">
                <Sparkles size={11} /> Wingman visar
              </p>
              <p className="font-heading text-base sm:text-lg font-semibold text-ink leading-snug">
                {wingmanFocus.title}
              </p>
              {wingmanFocus.subtitle && (
                <p className="text-sm text-bark/80 mt-0.5">{wingmanFocus.subtitle}</p>
              )}
              {wingmanFocus.examples && wingmanFocus.examples.length > 0 && (
                <p className="text-xs text-bark/70 mt-1.5 italic">
                  Exempel: {wingmanFocus.examples.filter(Boolean).join(" · ")}
                </p>
              )}
              <p className="text-xs text-bark/70 mt-2">
                {wingmanFocus.ids && wingmanFocus.ids.length > 0
                  ? `Listan nedanför är filtrerad till just dessa ${wingmanFocus.ids.length} poster.`
                  : wingmanFocus.filter
                  ? `Listan visar bara poster med status \u201E${wingmanFocus.filter}\u201D.`
                  : "Listan nedanför visar de berörda posterna."}
              </p>
            </div>
            <button
              onClick={() => setWingmanFocus(null)}
              data-testid="wingman-focus-clear"
              className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-falu hover:text-falu-hover bg-white border border-falu/30 hover:border-falu/60 rounded px-2.5 py-1.5 transition-colors"
              title="Visa hela listan"
            >
              <X size={13} /> Visa alla
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div data-testid="admin-tabs" className="flex flex-wrap gap-1 mb-6 bg-white rounded-lg shadow-sm p-1">
        {(isMaster(user) ? MASTER_TABS : MODERATOR_TABS).map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => { setTab(t.key); setWingmanFocus(null); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md transition-all ${
              tab === t.key ? "bg-pine text-white" : "text-muted-foreground hover:bg-bone-alt"
            } ${highlightTab === t.key ? "ring-2 ring-falu ring-offset-2 ring-offset-white animate-pulse" : ""}`}
            title={t.master ? "Endast master" : undefined}
          >
            <t.icon size={16} /> {t.label}
            {t.master && <Shield size={11} className={tab === t.key ? "text-white/70" : "text-falu/60"} />}
          </button>
        ))}
      </div>

      {/* Users tab (master only) */}
      {tab === "users" && isMaster(user) && <UsersAdmin currentUser={user} />}
      {tab === "platser" && isMaster(user) && <PlatserAdmin />}
      {/* Build 96.6 — Papperskorg (master only) */}
      {tab === "papperskorg" && isMaster(user) && <PapperskorgPanel />}

      {/* Actions bar (Build 96.3 — döljs i karttipps-tabben eftersom sammelvyn har egna filter) */}
      {tab !== "andringsforslag" && tab !== "users" && tab !== "platser" && tab !== "karttipps" && tab !== "papperskorg" && (
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
            <span data-testid="admin-items-count">{items.length} poster</span>
            {/* Etappe 4.4 — "Bara utan koordinater"-filter — ersätter Marker-fixer */}
            {["karttipps", "foreningar", "tjanstekort", "vardagskort"].includes(tab) && (() => {
              const missingCount = items.filter(i => i.lat == null || i.lng == null).length;
              return (
                <label
                  className="inline-flex items-center gap-2 cursor-pointer select-none"
                  data-testid="missing-coords-filter-label"
                >
                  <input
                    type="checkbox"
                    data-testid="missing-coords-filter"
                    checked={missingCoordsOnly}
                    onChange={(e) => setMissingCoordsOnly(e.target.checked)}
                    className="rounded border-warm-border accent-falu"
                  />
                  <span className={missingCoordsOnly ? "text-falu font-medium" : ""}>
                    Bara utan koordinater
                    <span className="ml-1 text-xs text-bark/55">({missingCount})</span>
                  </span>
                </label>
              );
            })()}
            {/* Build 94c.1/.3 — Schnellsuche nach stabiler Bearbeitungsnummer (pro Collection) */}
            {["karttipps","tjanstekort","foreningar","vardagskort"].includes(tab) && (
              <label className="inline-flex items-center gap-2 select-none" data-testid="nr-search-label">
                <span className="text-xs uppercase tracking-wide text-bark/60">Nr:</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={nrSearch}
                  onChange={(e) => setNrSearch(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="t.ex. 12"
                  data-testid="nr-search-input"
                  className="w-20 text-sm border border-warm-border rounded px-2 py-1 bg-white"
                />
                {nrSearch && (
                  <button
                    type="button"
                    onClick={() => setNrSearch("")}
                    data-testid="nr-search-clear"
                    className="text-xs text-bark/60 hover:text-falu underline"
                  >
                    rensa
                  </button>
                )}
              </label>
            )}
            {/* Etappe 4.5 — Evenemang-kvalitetsfilter (Kommande / Pågående / Förbi / Utan datum / Avpublicerat) */}
            {tab === "evenemang" && (() => {
              // Stockholm-dagens datum (samma jämförelse som backend för konsistens)
              const todaySE = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" });
              const counts = items.reduce((acc, e) => {
                if (!e.datum) acc.utan++;
                else if (e.datum < todaySE) acc.forbi++;
                else if (e.datum === todaySE) acc.pagaende++;
                else acc.kommande++;
                if (e.status && e.status !== "Publicerat") acc.avpub++;
                return acc;
              }, { kommande: 0, pagaende: 0, forbi: 0, utan: 0, avpub: 0 });
              return (
                <label className="inline-flex items-center gap-2 select-none">
                  <span className="text-xs uppercase tracking-wide text-bark/60">Visa:</span>
                  <select
                    data-testid="event-status-filter"
                    value={eventStatusFilter}
                    onChange={(e) => setEventStatusFilter(e.target.value)}
                    className="text-sm border border-warm-border rounded px-2 py-1 bg-white"
                  >
                    <option value="all">{`Alla (${items.length})`}</option>
                    <option value="kommande">{`Kommande (${counts.kommande})`}</option>
                    <option value="pagaende">{`Pågående idag (${counts.pagaende})`}</option>
                    <option value="forbi">{`Förbi / vergangen (${counts.forbi})`}</option>
                    <option value="utan">{`Utan datum (${counts.utan})`}</option>
                    <option value="avpub">{`Avpublicerat / Utkast (${counts.avpub})`}</option>
                  </select>
                </label>
              );
            })()}
          </div>
          <button
            data-testid="admin-create-btn"
            onClick={() => { setCreating(true); setEditId(null); setEditData({}); }}
            className="flex items-center gap-2 bg-falu hover:bg-falu-hover text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
          >
            <Plus size={16} /> {tab === "bilder" ? "Lägg till bild" : "Skapa ny"}
          </button>
        </div>
      )}

      {/* Create form */}
      {creating && (
        <div data-testid="admin-create-form" className="bg-white rounded-lg shadow-sm p-5 mb-4">
          <h3 className="font-heading text-lg font-medium text-ink mb-4">{tab === "bilder" ? "Lägg till bild" : "Skapa ny"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {getFields().map((f) => {
              // Build 95 — adminOnly-fält syns för alla, men non-admins ser dem read-only.
              const locked = f.adminOnly && !isMaster(user);
              return (
              <div key={f.key} className={(f.type === "textarea" || f.type === "kortbeskrivning" || f.type === "location" || f.type === "tags") ? "sm:col-span-2" : ""}>
                {f.type !== "tags" && (
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    {f.label}
                    {f.adminOnly && <span className="ml-1 text-[10px] text-falu/70 uppercase tracking-wider">admin</span>}
                    {f.required && <span className="text-falu ml-1" title="Pflichtfält">*</span>}
                    {f.type === "kortbeskrivning" && (
                      <span className={`ml-2 font-mono ${(editData[f.key] || "").length > 300 ? "text-falu font-bold" : "text-bark/60"}`}>
                        {(editData[f.key] || "").length} / 300
                      </span>
                    )}
                  </label>
                )}
                {f.hint && f.type !== "tags" && <p className="text-[11px] text-bark/60 mb-1.5 -mt-1">{f.hint}</p>}
                {f.type === "textarea" ? (
                  <textarea value={editData[f.key] || ""} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))} rows={2} disabled={locked} className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white disabled:bg-bone-alt/40 disabled:text-bark/55" data-testid={`field-${f.key}`} />
                ) : f.type === "kortbeskrivning" ? (
                  <textarea value={editData[f.key] || ""} maxLength={300} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))} rows={3} placeholder="En kort sammanfattning som visas i listor..." className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white" data-testid={`field-${f.key}`} />
                ) : f.type === "select" ? (
                  <select value={editData[f.key] || ""} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))} disabled={locked} className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white disabled:bg-bone-alt/40 disabled:text-bark/55" data-testid={`field-${f.key}`}>
                    <option value="">{f.emptyLabel || "Välj..."}</option>
                    {f.options.map(o => {
                      const val = typeof o === "string" ? o : o.value;
                      const lbl = typeof o === "string" ? o : o.label;
                      return <option key={val || `__empty__`} value={val}>{lbl}</option>;
                    })}
                  </select>
                ) : f.type === "location" ? (
                  <LocationPicker
                    lat={typeof editData.lat === "number" ? editData.lat : null}
                    lng={typeof editData.lng === "number" ? editData.lng : null}
                    adress={editData.adress || ""}
                    onChange={({ lat, lng, adress }) => setEditData(p => ({ ...p, lat, lng, adress }))}
                  />
                ) : f.type === "tags" ? (
                  <TagInput
                    value={editData[f.key] || ""}
                    onChange={(v) => setEditData(p => ({ ...p, [f.key]: v }))}
                    testid={`field-${f.key}`}
                  />
                ) : f.type === "checkbox" ? (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!editData[f.key]} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.checked }))} disabled={locked} className="rounded border-warm-border" data-testid={`field-${f.key}`} />
                    <span className="text-bark/75">Aktiverad</span>
                  </label>
                ) : f.type === "readonly" ? (
                  <input type="text" value={editData[f.key] || "(sätts automatiskt vid spara)"} disabled className="w-full px-3 py-2 border border-warm-border/40 rounded text-sm bg-bone-alt/40 text-bark/55" data-testid={`field-${f.key}`} />
                ) : (f.type === "date" || f.type === "time") ? (
                  <input type={f.type} value={editData[f.key] || ""} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))} disabled={locked} className={`w-full px-3 py-2 border rounded text-sm bg-white disabled:bg-bone-alt/40 disabled:text-bark/55 ${f.required && !(editData[f.key] || "").trim() ? "border-falu/60" : "border-warm-border"}`} data-testid={`field-${f.key}`} />
                ) : (
                  <input type="text" value={editData[f.key] || ""} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))} disabled={locked} className={`w-full px-3 py-2 border rounded text-sm bg-white disabled:bg-bone-alt/40 disabled:text-bark/55 ${f.required && !(editData[f.key] || "").trim() ? "border-falu/60" : "border-warm-border"}`} data-testid={`field-${f.key}`} />
                )}
              </div>
              );
            })}
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleCreate} className="bg-pine hover:bg-pine-hover text-white text-sm font-medium px-4 py-2 rounded transition-colors">Spara</button>
            <button onClick={() => setCreating(false)} className="text-sm text-muted-foreground hover:text-ink px-4 py-2">Avbryt</button>
          </div>
        </div>
      )}

      {/* (Wingman-Fokus-Banner ovan ersätter den gamla status-filter-banner) */}

      {/* Build 96.2 — Admin Platser-sammelvy: visas överst i karttipps-tab,
          aggregerar alla 4 collections och dispatchar edit till rätt tab. */}
      {tab === "karttipps" && (
        <PlatserOversiktPanel
          onOpenEdit={handleOpenEditFromOversikt}
          onCreateKarttipp={() => { setCreating(true); setEditId(null); setEditData({}); }}
          currentUser={user}
        />
      )}

      {/* Items list */}
      {tab === "users" || tab === "platser" || tab === "papperskorg" ? null : (() => {
        // Filtrera enligt wingman-fokus: prio 1 = explicit ids, prio 2 = status, annars hela listan
        let focusedItems = (() => {
          if (!wingmanFocus || wingmanFocus.tab !== tab) return items;
          if (wingmanFocus.ids && wingmanFocus.ids.length > 0) {
            const idSet = new Set(wingmanFocus.ids);
            return items.filter(i => idSet.has(i.id));
          }
          if (wingmanFocus.filter) return items.filter(i => i.status === wingmanFocus.filter);
          return items;
        })();
        // Build 96.3 — Karttipps-tab: dölj den gamla cardlistan helt. Visa bara
        // det Karttipp som just nu redigeras (från sammelvy eller "Skapa ny").
        // Sammelvyn är navigationen — den gamla listan blev visuellt dubblerad.
        if (tab === "karttipps") {
          if (creating) {
            // skapa-formuläret renderas senare via "creating"-state, ingen lista här
            focusedItems = [];
          } else if (editId) {
            focusedItems = focusedItems.filter(i => i.id === editId);
          } else {
            focusedItems = [];
          }
        }
        // Etappe 4.4 — efter wingman-filter: filtrera bort poster med koordinater om "Bara utan koordinater" är aktiv
        if (missingCoordsOnly && ["karttipps", "foreningar", "tjanstekort", "vardagskort"].includes(tab)) {
          focusedItems = focusedItems.filter(i => i.lat == null || i.lng == null);
        }
        // Build 94c.1/.3 — Nr-Schnellsuche pro Collection (Prefix-Match)
        const NR_FIELD_BY_TAB = {
          karttipps: "karttipp_nr",
          tjanstekort: "foretag_nr",
          foreningar: "forening_nr",
          vardagskort: "vardagskort_nr",
        };
        const nrField = NR_FIELD_BY_TAB[tab];
        if (nrField && nrSearch.trim()) {
          const needle = nrSearch.trim();
          focusedItems = focusedItems.filter(i =>
            i[nrField] != null && String(i[nrField]).startsWith(needle)
          );
        }
        // Etappe 4.5 — Evenemang-kvalitetsfilter
        if (tab === "evenemang" && eventStatusFilter !== "all") {
          const todaySE = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" });
          focusedItems = focusedItems.filter(e => {
            if (eventStatusFilter === "kommande") return e.datum && e.datum > todaySE && (e.status === "Publicerat" || !e.status);
            if (eventStatusFilter === "pagaende") return e.datum === todaySE && (e.status === "Publicerat" || !e.status);
            if (eventStatusFilter === "forbi") return e.datum && e.datum < todaySE;
            if (eventStatusFilter === "utan") return !e.datum;
            if (eventStatusFilter === "avpub") return e.status && e.status !== "Publicerat";
            return true;
          });
        }
        if (loading) return <p className="text-muted-foreground py-8">Laddar...</p>;
        // Etappe 4.9 — alphabetisk sortering (svensk locale) för all moderationslistor.
        // Använd primärt namn-/titel-fält beroende på collection. Datum-baserade tabbar (evenemang, anslag) behåller sin
        // tidsordning från backend.
        const SORT_KEY = {
          foreningar: "namn",
          tjanstekort: "foretagsnamn",
          vardagskort: "rubrik",
          karttipps: "namn",
          bilder: "rubrik",
        };
        const sortKey = SORT_KEY[tab];
        if (sortKey) {
          focusedItems = [...focusedItems].sort((a, b) =>
            (a[sortKey] || "").localeCompare(b[sortKey] || "", "sv", { sensitivity: "base" })
          );
        }
        if (focusedItems.length === 0) {
          return (
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <p className="text-muted-foreground">
                {wingmanFocus && wingmanFocus.tab === tab
                  ? "Ingen post matchar Wingman-filtret längre. Bra jobbat \u2013 du kan nu klicka \u201EVisa alla\u201D ovan."
                  : "Inga poster."}
              </p>
            </div>
          );
        }
        if (tab === "andringsforslag") return (
        <div data-testid="andra-list" className="flex flex-col gap-3">
          {focusedItems.map((f) => (
            <div
              key={f.id}
              data-testid={`andra-item-${f.id}`}
              className={`bg-white rounded-lg shadow-sm p-4 border-l-4 ${
                f.status === "Hanterat" ? "border-pine/30" : "border-falu"
              }`}
            >
              {editId === f.id ? (
                /* Edit mode för ändringsförslag */
                <div>
                  <h4 className="font-heading text-base font-medium text-ink mb-3">Redigera ändringsförslag</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Typ</label>
                      <select value={editData.typ ?? ""} onChange={e => setEditData(p => ({ ...p, typ: e.target.value }))} className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white" data-testid={`andra-edit-typ-${f.id}`}>
                        {["foretag","forening","vardagskort","evenemang","anslag"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
                      <select value={editData.status ?? ""} onChange={e => setEditData(p => ({ ...p, status: e.target.value }))} className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white" data-testid={`andra-edit-status-${f.id}`}>
                        {["Inskickat","Hanterat","Avvisat"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Referens (namn)</label>
                      <input type="text" value={editData.referens_namn ?? ""} onChange={e => setEditData(p => ({ ...p, referens_namn: e.target.value }))} className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white" data-testid={`andra-edit-referens-${f.id}`} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Meddelande</label>
                      <textarea value={editData.meddelande ?? ""} onChange={e => setEditData(p => ({ ...p, meddelande: e.target.value }))} rows={4} className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white" data-testid={`andra-edit-meddelande-${f.id}`} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Avsändare namn</label>
                      <input type="text" value={editData.avsandare_namn ?? ""} onChange={e => setEditData(p => ({ ...p, avsandare_namn: e.target.value }))} className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Avsändare e-post</label>
                      <input type="text" value={editData.avsandare_epost ?? ""} onChange={e => setEditData(p => ({ ...p, avsandare_epost: e.target.value }))} className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Avsändare telefon</label>
                      <input type="text" value={editData.avsandare_telefon ?? ""} onChange={e => setEditData(p => ({ ...p, avsandare_telefon: e.target.value }))} className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white" />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`andra-save-${f.id}`} onClick={() => handleSaveEdit(f.id)} className="flex items-center gap-1 bg-pine hover:bg-pine-hover text-white text-sm px-3 py-1.5 rounded"><Check size={14} /> Spara</button>
                    <button onClick={() => setEditId(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-ink px-3 py-1.5"><X size={14} /> Avbryt</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-bone-alt text-muted-foreground font-semibold">
                          {f.typ}
                        </span>
                        <span
                          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold ${
                            f.status === "Hanterat"
                              ? "bg-pine/10 text-pine"
                              : "bg-falu/10 text-falu"
                          }`}
                        >
                          {f.status}
                        </span>
                      </div>
                      <h3 className="font-heading font-semibold text-ink text-base mb-1">{f.referens_namn}</h3>
                      <p className="text-xs text-muted-foreground mb-2">
                        {new Date(f.inskicksdatum).toLocaleString("sv-SE")} · från {f.avsandare_namn}
                        {f.avsandare_epost && <> · <a href={`mailto:${f.avsandare_epost}`} className="text-falu hover:underline">{f.avsandare_epost}</a></>}
                        {f.avsandare_telefon && <> · <a href={`tel:${f.avsandare_telefon}`} className="text-falu hover:underline">{f.avsandare_telefon}</a></>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {f.status !== "Hanterat" && (
                        <button
                          data-testid={`andra-mark-${f.id}`}
                          onClick={() => handleMarkAndringsforslag(f.id)}
                          className="flex items-center gap-1 bg-pine hover:bg-pine-hover text-white text-sm px-3 py-1.5 rounded transition-colors"
                          title="Markera som hanterat"
                        >
                          <Check size={14} /> Hanterat
                        </button>
                      )}
                      <button
                        data-testid={`andra-edit-${f.id}`}
                        onClick={() => { setEditId(f.id); setEditData({ ...f }); }}
                        className="p-2 text-muted-foreground hover:text-ink rounded hover:bg-bone-alt transition-colors"
                        title="Redigera"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        data-testid={`andra-delete-${f.id}`}
                        onClick={() => handleDelete(f.id, `Ändringsförslag: ${f.referens_namn}`)}
                        className="p-2 text-muted-foreground hover:text-falu hover:bg-falu/10 rounded transition-colors"
                        title="Ta bort"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-ink/90 leading-relaxed whitespace-pre-wrap bg-bone-alt/40 rounded p-3">
                    {f.meddelande}
                  </p>
                  {f.hanterat_av && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Hanterat av {f.hanterat_av}
                      {f.hanterat_datum && ` · ${new Date(f.hanterat_datum).toLocaleString("sv-SE")}`}
                    </p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        );
        return (
        <div className="flex flex-col gap-2">
          {focusedItems.map((item) => (
            <div key={item.id} data-testid={`admin-item-${item.id}`} className={`bg-white rounded-lg shadow-sm p-4 transition-colors ${highlightItemId === item.id ? "ring-2 ring-pine ring-offset-2 bg-pine/5" : ""}`}>
              {editId === item.id ? (
                /* Edit mode */
                <div>
                  {/* Etappe 3-fix — synlig "Senast ändrad"-banner högst upp + Etappe 4.2 — Visa ändringshistorik-knapp */}
                  <div className="mb-3 px-3 py-2 bg-bone-alt/50 border-l-2 border-pine/40 rounded-sm text-xs text-bark/75 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {/* Build 94c.1/.3 — Nr direkt im Edit-Banner sichtbar (pro Collection) */}
                    {(() => {
                      const NR_INFO = {
                        karttipps: { field: "karttipp_nr", label: "Karttipp nr" },
                        tjanstekort: { field: "foretag_nr", label: "Företag nr" },
                        foreningar: { field: "forening_nr", label: "Förening nr" },
                        vardagskort: { field: "vardagskort_nr", label: "Vardagskort nr" },
                      }[tab];
                      if (!NR_INFO) return null;
                      const nr = item[NR_INFO.field];
                      if (nr == null) return null;
                      return (
                        <span
                          data-testid={`item-nr-edit-${item.id}`}
                          className="font-mono font-semibold text-bark bg-parchment border border-warm-border rounded px-2 py-0.5"
                          title="Stabil bearbetningsnummer — ändras aldrig"
                        >
                          {NR_INFO.label} #{nr}
                        </span>
                      );
                    })()}
                    <span>
                      <span className="font-medium">Senast ändrad:</span>{" "}
                      {item.senast_andrad
                        ? <span data-testid={`senast-andrad-${item.id}`}>{new Date(item.senast_andrad).toLocaleString("sv-SE")} av <strong>{item.andrad_av || "okänd"}</strong></span>
                        : <span data-testid={`senast-andrad-${item.id}`} className="italic text-bark/55">saknas</span>}
                      {Array.isArray(item.change_history) && item.change_history.length > 1 && (
                        <span className="ml-2 text-bark/55">· {item.change_history.length} ändringar totalt</span>
                      )}
                    </span>
                    {/* Etappe 4.2 + 4.3 — Visa ändringshistorik (endast för audit-trail-tabbar) */}
                    {["karttipps", "foreningar", "tjanstekort", "evenemang", "vardagskort", "anslag"].includes(tab) && (
                      <button
                        type="button"
                        data-testid={`show-history-${item.id}`}
                        onClick={() => setHistoryTarget({ collection: tab, docId: item.id })}
                        className="ml-auto inline-flex items-center gap-1 text-pine hover:text-pine-hover underline-offset-2 hover:underline font-medium"
                      >
                        Visa ändringshistorik
                      </button>
                    )}
                  </div>
                  {/* Build 94c.4 — Warnung wenn Event mit legacy-Datums-/Tids-Format geöffnet wird */}
                  {tab === "evenemang" && (() => {
                    const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
                    const TIME_RE = /^\d{2}:\d{2}$/;
                    const TIME_LEGACY = /^\d{2}:\d{2}\s*[–\-—/]\s*\d{2}:\d{2}$/;
                    const bad = [];
                    if (item.datum && !DATE_RE.test(String(item.datum).trim())) bad.push(`datum="${item.datum}"`);
                    if (item.datum_slut && !DATE_RE.test(String(item.datum_slut).trim())) bad.push(`datum_slut="${item.datum_slut}"`);
                    if (item.tid && !TIME_RE.test(String(item.tid).trim()) && !TIME_LEGACY.test(String(item.tid).trim())) bad.push(`tid="${item.tid}"`);
                    if (item.tid_slut && !TIME_RE.test(String(item.tid_slut).trim()) && !TIME_LEGACY.test(String(item.tid_slut).trim())) bad.push(`tid_slut="${item.tid_slut}"`);
                    if (bad.length === 0) return null;
                    return (
                      <div data-testid={`legacy-format-warning-${item.id}`} className="mb-3 px-3 py-2 bg-amber-50 border-l-4 border-amber-500 rounded-sm text-sm text-amber-900">
                        <strong>Det här evenemanget har ett äldre datumformat.</strong> Korrigera startdatum och slutdatum innan du sparar.
                        <div className="mt-1 font-mono text-xs text-amber-800">Berörda fält: {bad.join(" · ")}</div>
                      </div>
                    );
                  })()}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {getFields().map((f) => {
                      // Build 95 — adminOnly-fält syns för alla men non-admins ser dem read-only.
                      const locked = f.adminOnly && !isMaster(user);
                      return (
                      <div key={f.key} className={(f.type === "textarea" || f.type === "kortbeskrivning" || f.type === "location" || f.type === "tags") ? "sm:col-span-2" : ""}>
                        {f.type !== "tags" && (
                          <label className="block text-xs font-medium text-muted-foreground mb-1">
                            {f.label}
                            {f.adminOnly && <span className="ml-1 text-[10px] text-falu/70 uppercase tracking-wider">admin</span>}
                            {f.required && <span className="text-falu ml-1" title="Pflichtfält">*</span>}
                            {f.type === "kortbeskrivning" && (
                              <span className={`ml-2 font-mono ${(editData[f.key] || "").length > 300 ? "text-falu font-bold" : "text-bark/60"}`}>
                                {(editData[f.key] || "").length} / 300
                              </span>
                            )}
                          </label>
                        )}
                        {f.hint && f.type !== "tags" && <p className="text-[11px] text-bark/60 mb-1.5 -mt-1">{f.hint}</p>}
                        {f.type === "textarea" ? (
                          <textarea value={editData[f.key] ?? ""} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))} rows={2} disabled={locked} className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white disabled:bg-bone-alt/40 disabled:text-bark/55" data-testid={`edit-field-${f.key}-${item.id}`} />
                        ) : f.type === "kortbeskrivning" ? (
                          <textarea value={editData[f.key] ?? ""} maxLength={300} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))} rows={3} placeholder="En kort sammanfattning som visas i listor..." className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white" data-testid={`edit-field-${f.key}-${item.id}`} />
                        ) : f.type === "select" ? (
                          <select value={editData[f.key] ?? ""} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))} disabled={locked} className="w-full px-3 py-2 border border-warm-border rounded text-sm bg-white disabled:bg-bone-alt/40 disabled:text-bark/55" data-testid={`edit-field-${f.key}-${item.id}`}>
                            <option value="">{f.emptyLabel || "Välj..."}</option>
                            {f.options.map(o => {
                              const val = typeof o === "string" ? o : o.value;
                              const lbl = typeof o === "string" ? o : o.label;
                              return <option key={val || `__empty__`} value={val}>{lbl}</option>;
                            })}
                          </select>
                        ) : f.type === "location" ? (
                          <LocationPicker
                            lat={typeof editData.lat === "number" ? editData.lat : null}
                            lng={typeof editData.lng === "number" ? editData.lng : null}
                            adress={editData.adress || ""}
                            onChange={({ lat, lng, adress }) => setEditData(p => ({ ...p, lat, lng, adress }))}
                          />
                        ) : f.type === "tags" ? (
                          <TagInput
                            value={editData[f.key] ?? ""}
                            onChange={(v) => setEditData(p => ({ ...p, [f.key]: v }))}
                            testid={`edit-field-${f.key}-${item.id}`}
                          />
                        ) : f.type === "checkbox" ? (
                          <label className="inline-flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={!!editData[f.key]} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.checked }))} disabled={locked} className="rounded border-warm-border" data-testid={`edit-field-${f.key}-${item.id}`} />
                            <span className="text-bark/75">Aktiverad</span>
                          </label>
                        ) : f.type === "readonly" ? (
                          <input type="text" value={editData[f.key] ? new Date(editData[f.key]).toLocaleString("sv-SE") : "(sätts automatiskt vid spara)"} disabled className="w-full px-3 py-2 border border-warm-border/40 rounded text-sm bg-bone-alt/40 text-bark/55" data-testid={`edit-field-${f.key}-${item.id}`} />
                        ) : (f.type === "date" || f.type === "time") ? (
                          <input type={f.type} value={editData[f.key] || ""} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))} disabled={locked} className={`w-full px-3 py-2 border rounded text-sm bg-white disabled:bg-bone-alt/40 disabled:text-bark/55 ${f.required && !(editData[f.key] || "").trim() ? "border-falu/60" : "border-warm-border"}`} data-testid={`edit-field-${f.key}-${item.id}`} />
                        ) : (
                          <input type="text" value={editData[f.key] ?? ""} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))} disabled={locked} className={`w-full px-3 py-2 border rounded text-sm bg-white disabled:bg-bone-alt/40 disabled:text-bark/55 ${f.required && !(editData[f.key] || "").trim() ? "border-falu/60" : "border-warm-border"}`} data-testid={`edit-field-${f.key}-${item.id}`} />
                        )}
                      </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`save-${item.id}`} onClick={() => handleSaveEdit(item.id)} className="flex items-center gap-1 bg-pine hover:bg-pine-hover text-white text-sm px-3 py-1.5 rounded"><Check size={14} /> Spara</button>
                    <button onClick={() => setEditId(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-ink px-3 py-1.5"><X size={14} /> Avbryt</button>
                  </div>
                  {(tab === "tjanstekort" || tab === "foreningar" || tab === "evenemang" || tab === "vardagskort" || tab === "anslag" || tab === "karttipps") && (
                    <GalleriEditor
                      collection={tab}
                      itemId={item.id}
                      bilder={item.galleri || []}
                      galleriAudio={item.galleri_audio || {}}
                      onChange={(newList) => {
                        // Optimistisk update lokalt så admin ser det direkt
                        item.galleri = newList;
                        setEditData(p => ({ ...p }));
                      }}
                      onAudioChange={(newMap) => {
                        item.galleri_audio = newMap;
                        setEditData(p => ({ ...p }));
                      }}
                    />
                  )}
                </div>
              ) : (
                /* View mode */
                <div className="flex items-start justify-between gap-3">
                  {Array.isArray(item.galleri) && item.galleri.length > 0 && (
                    <div className="shrink-0 flex gap-1" data-testid={`inbox-media-${item.id}`}>
                      {item.galleri.slice(0, 3).map((url, i) => {
                        const full = `${process.env.REACT_APP_BACKEND_URL}${url}`;
                        const isVideo = /\.(mp4|mov|webm|m4v)$/i.test(url);
                        return isVideo ? (
                          <div key={i} className="relative w-12 h-12 rounded-sm border border-warm-border bg-ink overflow-hidden">
                            <video src={`${full}#t=0.5`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                            <span className="absolute bottom-0.5 left-0.5 text-[8px] font-bold uppercase px-1 py-0.5 rounded-sm bg-parchment/90 text-bark pointer-events-none">Video</span>
                          </div>
                        ) : (
                          <img key={i} src={full} alt="" className="w-12 h-12 object-cover rounded-sm border border-warm-border" />
                        );
                      })}
                      {item.galleri.length > 3 && (
                        <span className="self-end text-[10px] text-muted-foreground ml-1">+{item.galleri.length - 3}</span>
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      {/* Build 94c.1/.3 — stabile menschenlesbare Nummer pro Collection */}
                      {(() => {
                        const NR_FIELD = {
                          karttipps: "karttipp_nr",
                          tjanstekort: "foretag_nr",
                          foreningar: "forening_nr",
                          vardagskort: "vardagskort_nr",
                        }[tab];
                        const nr = NR_FIELD ? item[NR_FIELD] : null;
                        return nr != null ? (
                          <span
                            data-testid={`item-nr-badge-${item.id}`}
                            className="shrink-0 font-mono text-[11px] font-semibold text-bark bg-bone-alt border border-warm-border rounded px-1.5 py-0.5"
                            title="Stabil bearbetningsnummer — ändras aldrig"
                          >
                            #{nr}
                          </span>
                        ) : null;
                      })()}
                      <h3 className="font-medium text-ink text-sm truncate">{getTitle(item)}</h3>
                      <span className={`shrink-0 text-xs px-2 py-0.5 rounded ${isPublished(item) ? "bg-pine/10 text-pine" : "bg-bone-alt text-muted-foreground"}`}>
                        {getStatus(item)}
                      </span>
                      {item.auto_imported && (
                        <span
                          data-testid={`auto-import-badge-${item.id}`}
                          title={`Importerad från ${item.source_name || "extern källa"}${item.last_checked_at ? ` (senast kontrollerad ${new Date(item.last_checked_at).toLocaleDateString("sv-SE")})` : ""}`}
                          className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-falu/10 text-falu border border-falu/20"
                        >
                          Auto · {item.source_name || "extern källa"}
                        </span>
                      )}
                      {item.auto_archived_at && (
                        <span
                          data-testid={`auto-archived-badge-${item.id}`}
                          title={`Auto-arkiverad ${new Date(item.auto_archived_at).toLocaleDateString("sv-SE")}${item.auto_archive_reason ? ` (${item.auto_archive_reason})` : ""}. Du kan publicera igen om det var ett misstag.`}
                          className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-bark/10 text-bark border border-bark/20"
                        >
                          Auto-arkiverad
                        </span>
                      )}
                      {(item.possible_duplicate_of || (item.auto_review_reason && item.auto_review_reason.toLowerCase().includes("dubblett"))) && (
                        <span
                          data-testid={`duplicate-badge-${item.id}`}
                          title={item.auto_review_reason || "Möjlig dubblett mot befintligt evenemang"}
                          className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-falu text-parchment"
                        >
                          ⚠ Möjlig dubblett
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {item.ort && `${item.ort} · `}
                      {item.beskrivning?.slice(0, 80)}
                      {item.beskrivning?.length > 80 ? "..." : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Build 96.17 — Enhetlig moderations-livscykel (samma icons + ordning som PlatserOversiktPanel).
                        Endast för tabs med moderation_status-support. Rör aldrig publiceringsstatus. */}
                    {["karttipps","tjanstekort","foreningar","vardagskort","evenemang","anslag","bilder"].includes(tab) && (() => {
                      const ms = item.moderation_status || "aktiv";
                      const isAdmin = isMaster(user);
                      const MODERATION_META = {
                        aktiv: { label: "Aktiv", color: "bg-pine/10 text-pine border-pine/20", Icon: RotateCw },
                        granskas: { label: "Granskas", color: "bg-amber-100 text-amber-800 border-amber-200", Icon: EyeOff },
                        deaktiverad: { label: "Deaktiverad", color: "bg-slate-100 text-slate-700 border-slate-200", Icon: PauseCircle },
                        arkiverad: { label: "Arkiverad", color: "bg-neutral-200 text-neutral-800 border-neutral-300", Icon: Archive },
                      };
                      const meta = MODERATION_META[ms] || MODERATION_META.aktiv;
                      const BadgeIcon = meta.Icon;
                      const btnBase = "p-1.5 rounded transition-colors disabled:opacity-40";
                      const arkiveradLocked = ms === "arkiverad" && !isAdmin;
                      return (
                        <>
                          <span
                            data-testid={`ms-badge-${tab}-${item.id}`}
                            title={ms === "aktiv" ? "Aktiv — syns publikt" :
                                   ms === "granskas" ? "I granskning — dolt publikt tills kontrollerad" :
                                   ms === "deaktiverad" ? "Deaktiverad — dolt publikt, data bevarad" :
                                   "Arkiverad — historisk post (endast admin får reaktivera)"}
                            className={`hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${meta.color}`}
                          >
                            <BadgeIcon size={11} />{meta.label}
                          </span>
                          {ms !== "aktiv" && (
                            <button
                              type="button"
                              onClick={() => setItemModerationStatus(item.id, "aktiv")}
                              disabled={arkiveradLocked}
                              data-testid={`ms-set-aktiv-${tab}-${item.id}`}
                              title={arkiveradLocked ? "Endast admin kan reaktivera arkiverade" : "Aktivera – syns publikt igen"}
                              className={`${btnBase} text-pine hover:bg-pine/10`}
                            >
                              <RotateCw size={14} />
                            </button>
                          )}
                          {ms !== "granskas" && (
                            <button
                              type="button"
                              onClick={() => setItemModerationStatus(item.id, "granskas")}
                              disabled={arkiveradLocked}
                              data-testid={`ms-set-granskas-${tab}-${item.id}`}
                              title="Sätt i granskning – döljs publikt tills kontrollerad"
                              className={`${btnBase} text-amber-700 hover:bg-amber-100`}
                            >
                              <EyeOff size={14} />
                            </button>
                          )}
                          {ms !== "deaktiverad" && (
                            <button
                              type="button"
                              onClick={() => setItemModerationStatus(item.id, "deaktiverad")}
                              disabled={arkiveradLocked}
                              data-testid={`ms-set-deaktiverad-${tab}-${item.id}`}
                              title="Deaktivera – döljs publikt, all data bevaras"
                              className={`${btnBase} text-slate-600 hover:bg-slate-200`}
                            >
                              <PauseCircle size={14} />
                            </button>
                          )}
                          {ms !== "arkiverad" && isAdmin && (
                            <button
                              type="button"
                              onClick={() => setItemModerationStatus(item.id, "arkiverad")}
                              data-testid={`ms-set-arkiverad-${tab}-${item.id}`}
                              title="Arkivera (endast admin) – historisk post"
                              className={`${btnBase} text-neutral-700 hover:bg-neutral-200`}
                            >
                              <Archive size={14} />
                            </button>
                          )}
                        </>
                      );
                    })()}
                    {tab !== "bilder" && (
                      <button
                        data-testid={`edit-${item.id}`}
                        onClick={() => { setEditId(item.id); setEditData(hydrateForUi(item)); setCreating(false); }}
                        className="p-2 text-muted-foreground hover:text-ink rounded hover:bg-bone-alt transition-colors"
                        title="Redigera"
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    {isPublished(item) ? (
                      <button
                        onClick={() => handleAction(item.id, "unpublish")}
                        data-testid={`unpublish-${item.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-falu border border-falu/40 hover:bg-falu/10 rounded transition-colors"
                        title="Avpublicera – tas bort från publik vy"
                      >
                        <EyeOff size={14} /> Avpublicera
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAction(item.id, "publish")}
                        data-testid={`publish-${item.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-pine border border-pine/40 hover:bg-pine/10 rounded transition-colors"
                        title="Publicera – gör synligt publikt"
                      >
                        <Eye size={14} /> Publicera
                      </button>
                    )}
                    <button
                      data-testid={`delete-${item.id}`}
                      onClick={() => handleDelete(item.id, getTitle(item), { item })}
                      className="p-2 text-muted-foreground hover:text-falu hover:bg-falu/10 rounded transition-colors"
                      title="Ta bort"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        );
      })()}
      {/* Etappe 4.2 — Change-history modal (rendert nur när historyTarget gesetzt) */}
      {historyTarget && (
        <ChangeHistoryViewer
          collection={historyTarget.collection}
          docId={historyTarget.docId}
          onClose={() => setHistoryTarget(null)}
        />
      )}
    </div>
  );
}

function UploadHealthLine({ health }) {
  if (!health || !health.last_upload_at) {
    return (
      <p data-testid="upload-health" className="text-xs text-muted-foreground/70 mt-1">
        Uppladdningar: ingen lyckad upload registrerad ännu.
      </p>
    );
  }
  const dt = new Date(health.last_upload_at);
  const diffMin = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 60000));
  const fmtAge =
    diffMin < 1 ? "just nu" :
    diffMin < 60 ? `${diffMin} min sedan` :
    diffMin < 24 * 60 ? `${Math.floor(diffMin / 60)} h sedan` :
    `${Math.floor(diffMin / (24 * 60))} d sedan`;
  const stale = diffMin > 24 * 60 * 7;
  return (
    <p
      data-testid="upload-health"
      className={`text-xs mt-1 ${stale ? "text-falu/80" : "text-muted-foreground/70"}`}
      title={`${health.media_typ || "okänd"} · ${health.saved_to_r2 ? "R2" : "lokal fallback"}`}
    >
      Senast lyckade upload: {dt.toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })} &middot; {fmtAge}
    </p>
  );
}

